import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: '#2c3e50', label: 'Midnight Blue' },
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' },
    { value: '#f44336', label: 'Red' }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Marketing Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "Any City, State",
  website: "linkedin.com/in/richard",
  profile: "Dynamic and creative Marketing Manager with 8+ years of experience in driving brand growth through innovative digital strategies. Expertise in SEO, content marketing, and leading high-performing teams to achieve significant ROI.",
};

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'contact',
  'expertise',
  'education'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'references',
  'languages',
  'accomplishments',
  'certifications',
  'softwareSkills',
  'volunteering',
  'interests',
  'websites',
  'additionalInfo'
];

const Resume31 = ({
  color = "#2c3e50",
  font = "Inter, Roboto, sans-serif",
  fontSize = "14px",
  headingSize = "22px",
  fontStyle = "normal",
  sectionSpacing = 30,
  paragraphSpacing = 20,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  designSettings = {},
  linkedIn = '',
}) => {
  // Merge design settings with props
  const mergedColor = designSettings.color || color;
  const mergedFont = designSettings.font || font;
  const mergedFontSize = designSettings.fontSize || fontSize;
  const mergedHeadingSize = designSettings.headingSize || headingSize;
  const mergedFontStyle = designSettings.fontStyle || fontStyle;
  const mergedSectionSpacing = designSettings.sectionSpacing !== undefined ? designSettings.sectionSpacing : sectionSpacing;
  const mergedParagraphSpacing = designSettings.paragraphSpacing !== undefined ? designSettings.paragraphSpacing : paragraphSpacing;
  const mergedLineSpacing = designSettings.lineSpacing !== undefined ? designSettings.lineSpacing : lineSpacing;
  const mergedTopBottomMargin = designSettings.topBottomMargin !== undefined ? designSettings.topBottomMargin : topBottomMargin;
  const mergedSideMargins = designSettings.sideMargins !== undefined ? designSettings.sideMargins : sideMargins;
  const mergedLineWeight = designSettings.lineWeight !== undefined ? designSettings.lineWeight : lineWeight;
  const mergedPageSize = designSettings.pageSize || pageSize;

  const page = pageSizeMap[mergedPageSize] || pageSizeMap.A4;

  const data = {
    ...defaultData,
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const mainContentRef = useRef(null);

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── SHARED STYLE HELPERS ───────────────────────────────────────────────────

  const globalFontStyle = {
    fontFamily: mergedFont,
    fontSize: mergedFontSize,
    fontStyle: mergedFontStyle,
    lineHeight: `${mergedLineSpacing}px`,
    // KEY FIX: prevent any text from ever escaping its column
    wordBreak: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  const getSectionHeaderStyle = (headerColor = "#2c3e50") => ({
    ...globalFontStyle,
    fontSize: mergedHeadingSize,
    fontWeight: "bold",
    mb: `${mergedSectionSpacing / 2}px`,
    color: headerColor,
    borderBottom: `${mergedLineWeight}px solid ${headerColor}`,
    pb: 1,
    // Ensure heading never overflows
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  });

  const getContentSpacingStyle = () => ({
    mb: `${mergedSectionSpacing}px`,
    ...globalFontStyle,
    // Constrain the whole section block
    width: "100%",
    overflow: "hidden",
  });

  const getParagraphSpacingStyle = () => ({
    mb: `${mergedParagraphSpacing}px`,
    ...globalFontStyle,
    overflow: "hidden",
  });

  // ─── DATA LOADING ────────────────────────────────────────────────────────────

  useEffect(() => {
    const loadData = () => {
      const storedEntries = localStorage.getItem("educationEntries");
      if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

      const storedSkills = localStorage.getItem("skills");
      if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

      const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
      if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
      setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
      setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
      setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
      setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
      setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
      setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
      setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

      const userId = localStorage.getItem("userId");
      let sectionsData = {};

      if (userId) {
        const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const validItems = (parsedData.items || []).filter(item => item && item.trim() !== '');
              if (validItems.length > 0) sectionsData[sectionName] = validItems;
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }

      setCustomSections(sectionsData);

      const savedLeft = localStorage.getItem("resume31LeftSectionOrder");
      if (savedLeft) {
        setLeftSectionOrder(JSON.parse(savedLeft).filter(s => !s.startsWith('custom_')));
      } else {
        setLeftSectionOrder(defaultLeftSectionOrder);
      }

      const savedRight = localStorage.getItem("resume31RightSectionOrder");
      if (savedRight) {
        const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
        const filtered = JSON.parse(savedRight).filter(s => !s.startsWith('custom_'));
        if (!filtered.includes('websites')) {
          filtered.push('websites');
        }
        setRightSectionOrder([...filtered, ...currentCustomIds]);
      } else {
        const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
        setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
      }
    };

    loadData();
  }, []);

  const updateSectionOrders = useCallback((sectionsData) => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);

    setRightSectionOrder(prev => {
      const filtered = prev.filter(s => !s.startsWith('custom_'));
      const next = [...filtered, ...currentCustomIds];
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem("resume31RightSectionOrder", JSON.stringify(next));
        return next;
      }
      return prev;
    });

    setLeftSectionOrder(prev => {
      const filtered = prev.filter(s => !s.startsWith('custom_'));
      if (JSON.stringify(filtered) !== JSON.stringify(prev)) {
        localStorage.setItem("resume31LeftSectionOrder", JSON.stringify(filtered));
        return filtered;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (Object.keys(customSections).length > 0) {
      updateSectionOrders(customSections);
    }
  }, [customSections, updateSectionOrders]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── DRAG & DROP ─────────────────────────────────────────────────────────────

  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (dragData.side === 'left') newLeft.splice(dragData.index, 1);
    else newRight.splice(dragData.index, 1);
    newLeft.splice(targetIndex, 0, dragData.sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    localStorage.setItem("resume31LeftSectionOrder", JSON.stringify(newLeft));
    localStorage.setItem("resume31RightSectionOrder", JSON.stringify(newRight));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (dragData.side === 'left') newLeft.splice(dragData.index, 1);
    else newRight.splice(dragData.index, 1);
    newRight.splice(targetIndex, 0, dragData.sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    localStorage.setItem("resume31LeftSectionOrder", JSON.stringify(newLeft));
    localStorage.setItem("resume31RightSectionOrder", JSON.stringify(newRight));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    localStorage.setItem("resume31LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
  };

  const resetRightSectionOrder = () => {
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    const newOrder = [...defaultRightSectionOrder, ...customIds];
    setRightSectionOrder(newOrder);
    localStorage.setItem("resume31RightSectionOrder", JSON.stringify(newOrder));
  };

  const resetAllSectionOrder = () => {
    resetLeftSectionOrder();
    resetRightSectionOrder();
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  // ─── EDUCATION DATA ──────────────────────────────────────────────────────────

  const getEducationEntries = () => {
    const forResume = localStorage.getItem("educationEntriesForResume");
    if (forResume) return JSON.parse(forResume);

    const stored = localStorage.getItem("educationEntries");
    if (!stored) return [];

    return JSON.parse(stored).map(entry => {
      if (entry.educationLevel === "school") {
        return {
          id: entry.id,
          degree: `${entry.standard || ""}${entry.standard ? " Standard" : ""}`,
          fieldOfStudy: entry.stream || "General Education",
          schoolName: entry.schoolName || "",
          schoolLocation: entry.schoolLocation || "",
          gradMonth: "",
          gradYear: "",
          additionalCoursework: [
            entry.district && `${entry.district}`,
            entry.percentage && `Percentage: ${entry.percentage}%`,
          ].filter(Boolean).join(" | ")
        };
      }
      return {
        id: entry.id,
        degree: `${entry.degreeName || ""}${entry.programType ? ` (${entry.programType})` : ''}`,
        fieldOfStudy: entry.specialization || entry.degreeType || "",
        schoolName: entry.collegeName || "",
        schoolLocation: entry.collegeLocation || "",
        gradMonth: entry.gradMonth || "",
        gradYear: entry.gradYear || "",
        additionalCoursework: [
          entry.cgpa && `CGPA: ${entry.cgpa}`,
          entry.percentage && `Percentage: ${entry.percentage}%`,
          entry.grade && `Grade: ${entry.grade}`,
          entry.additionalCoursework
        ].filter(Boolean).join(" | ")
      };
    });
  };

  // ─── SECTION RENDERER ────────────────────────────────────────────────────────
  // KEY FIX: every text node gets wordBreak + overflowWrap; containers get
  // width:100% + overflow:hidden so nothing can bleed into the adjacent column.

  const renderSection = (sectionId, isSidebar = false) => {
    const textColor = isSidebar ? "white" : "inherit";
    const headingColor = isSidebar ? "white" : mergedColor;

    // Reusable inline text sx that every <Typography> inside sections uses
    const txtSx = {
      ...globalFontStyle,
      color: textColor,
      maxWidth: "100%",
    };

    // Section wrapper – the most important containment layer
    const sectionWrapSx = {
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      mb: `${mergedSectionSpacing}px`,
      boxSizing: "border-box",
    };

    // Sub-heading (CONTACT / EXPERTISE / EDUCATION labels in sidebar)
    const subHeadSx = {
      ...globalFontStyle,
      fontWeight: "bold",
      mb: 2,
      fontSize: mergedHeadingSize,
      borderBottom: `${mergedLineWeight}px solid ${isSidebar ? "rgba(255,255,255,0.2)" : mergedColor}`,
      pb: 1,
      fontFamily: mergedFont,
      color: textColor,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      wordBreak: "break-word",
      overflowWrap: "break-word",
    };

    switch (sectionId) {

      // ── CONTACT ──────────────────────────────────────────────────────────────
      case 'contact':
        return (
          <Box key="contact" sx={sectionWrapSx}>
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ mb: 5, textAlign: "center", width: "100%", overflow: "hidden" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "900",
                  mb: 1,
                  lineHeight: 1.2,
                  fontSize: `calc(${headingSize} * 1.5)`,
                  fontFamily: font,
                  color: textColor,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {data.firstName.toUpperCase()} <br /> {data.lastName.toUpperCase()}
              </Typography>
              <Typography sx={{
                fontSize: fontSize,
                letterSpacing: 2,
                opacity: 0.8,
                textTransform: "uppercase",
                fontFamily: font,
                color: textColor,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                width: "100%",
                maxWidth: "100%",
                ...globalFontStyle,
              }}>
                {data.currentPosition}
              </Typography>

              <Typography sx={subHeadSx} style={{ marginTop: "32px" }}>
                CONTACT
              </Typography>

              <Box sx={{
                mb: 4,
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                "& p": {
                  fontSize: fontSize,
                  mb: `${mergedParagraphSpacing / 2}px`,
                  display: "flex",
                  alignItems: "flex-start",
                  fontFamily: font,
                  justifyContent: "flex-start",
                  color: textColor,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  maxWidth: "100%",
                  overflow: "hidden",
                  ...globalFontStyle,
                },
              }}>
                {data.phone && <p>📞 <span style={{ marginLeft: 4, wordBreak: "break-all", overflowWrap: "break-word", flex: 1 }}>{data.phone}</span></p>}
                {data.email && <p>✉️ <span style={{ marginLeft: 4, wordBreak: "break-all", overflowWrap: "break-word", flex: 1 }}>{data.email}</span></p>}
                {data.city && <p>📍 <span style={{ marginLeft: 4, wordBreak: "break-word", overflowWrap: "break-word", flex: 1 }}>{data.city}</span></p>}
                {data.website && <p>🌐 <span style={{ marginLeft: 4, wordBreak: "break-all", overflowWrap: "break-word", flex: 1 }}>{data.website}</span></p>}
                {data.linkedIn && (
                  <p>
                    <LinkedInIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', flexShrink: 0 }} />
                    <span style={{ wordBreak: "break-all", overflowWrap: "break-word", flex: 1 }}>{data.linkedIn}</span>
                  </p>
                )}
              </Box>
            </Box>
          </Box>
        );

      // ── EXPERTISE ────────────────────────────────────────────────────────────
      case 'expertise':
        return (
          <Box key="expertise" sx={sectionWrapSx}>
            <Typography sx={subHeadSx}>EXPERTISE</Typography>
            <Box sx={{ mb: 4, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {(savedSkills.length > 0 ? savedSkills : [
                { name: "Digital Marketing", rating: 90 },
                { name: "Public Relations", rating: 80 },
                { name: "Team Leadership", rating: 85 },
                { name: "Market Analysis", rating: 75 }
              ]).map((skill) => (
                <Box key={skill.name} sx={{ mb: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                  <Typography sx={{ ...txtSx, mb: 0.5 }}>{skill.name}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={skill.rating || skill.val || 0}
                    sx={{
                      bgcolor: isSidebar ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": { bgcolor: isSidebar ? "white" : color },
                      height: 4,
                      borderRadius: 2,
                      width: "100%",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        );

      // ── EDUCATION ────────────────────────────────────────────────────────────
      case 'education': {
        const eduEntries = getEducationEntries();
        return eduEntries.length > 0 ? (
          <Box key="education" sx={sectionWrapSx}>
            <Typography sx={subHeadSx}>EDUCATION</Typography>
            <Box sx={{ mb: 4, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {eduEntries.map((edu, index) => (
                <Box key={edu.id || index} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                  <Typography sx={{ ...txtSx, fontWeight: "bold" }}>{edu.degree}</Typography>
                  <Typography sx={{ ...txtSx, opacity: 0.8 }}>{edu.schoolName}</Typography>
                  <Typography sx={{ ...txtSx, mb: 2, opacity: 0.6 }}>
                    {edu.gradYear || ""}{edu.gradMonth ? ` - ${edu.gradMonth}` : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;
      }

      // ── PROFILE ──────────────────────────────────────────────────────────────
      case 'profile':
        return (savedSummary || data.profile) ? (
          <Box key="profile" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              PROFESSIONAL PROFILE
            </Typography>
            <Typography sx={{
              ...txtSx,
              lineHeight: `${lineSpacing}px`,
              mb: `${sectionSpacing}px`,
            }}>
              {savedSummary || data.profile}
            </Typography>
          </Box>
        ) : null;

      // ── WORK EXPERIENCE ──────────────────────────────────────────────────────
      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <Box key="workExperience" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              WORK EXPERIENCE
            </Typography>
            <Box sx={{ mt: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                  {/* Title row – flex with wrapping so date never pushes title outside */}
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "4px",
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}>
                    <Typography sx={{
                      ...txtSx,
                      fontWeight: "bold",
                      fontSize: "16px",
                      flex: "1 1 auto",
                      minWidth: 0,
                    }}>
                      {work.jobTitle}
                    </Typography>
                    <Typography sx={{
                      ...globalFontStyle,
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: headingColor,
                      flex: "0 0 auto",
                      whiteSpace: "nowrap",
                    }}>
                      {formatDate(work.startMonth, work.startYear)} –{" "}
                      {work.current ? "PRESENT" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>

                  <Typography sx={{
                    ...txtSx,
                    color: isSidebar ? "rgba(255,255,255,0.8)" : "gray",
                    mb: 1.5,
                  }}>
                    {work.companyName || work.employer}{work.location ? ` | ${work.location}` : ""}
                  </Typography>

                  {work.description && (
                    <List sx={{ p: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                      {(work.description || '').split('\n').filter(line => line.trim()).map((line, idx) => (
                        <ListItem key={idx} sx={{ p: 0, mb: `${paragraphSpacing / 2}px`, alignItems: "flex-start", maxWidth: "100%", overflow: "hidden" }}>
                          <Box component="span" sx={{ color: headingColor, mr: 1, mt: "-2px", fontSize: "18px", flexShrink: 0 }}>•</Box>
                          <ListItemText
                            primary={line.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1").replace(/<u>(.*?)<\/u>/g, "$1")}
                            primaryTypographyProps={{
                              ...globalFontStyle,
                              color: textColor,
                              sx: { wordBreak: "break-word", overflowWrap: "break-word" }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null;

      // ── REFERENCES ───────────────────────────────────────────────────────────
      case 'references':
        return savedReferences.length > 0 ? (
          <Box key="references" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              REFERENCES
            </Typography>
            <Grid container spacing={3} sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", m: 0 }}>
              {savedReferences.map((ref, index) => {
                let refData;
                try { refData = typeof ref === 'string' ? JSON.parse(ref) : ref; }
                catch { refData = { name: ref, company: "", position: "", phone: "" }; }
                return (
                  <Grid key={index} item xs={6} sx={{ overflow: "hidden", maxWidth: "100%" }}>
                    <Typography sx={{ ...txtSx, fontWeight: "bold" }}>{refData.name}</Typography>
                    <Typography sx={{ ...txtSx, color: isSidebar ? "rgba(255,255,255,0.8)" : "gray" }}>
                      {refData.position}{refData.company ? `, ${refData.company}` : ''}
                    </Typography>
                    {refData.phone && (
                      <Typography sx={{ ...txtSx, mt: 0.5 }}>📞 {refData.phone}</Typography>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null;

      // ── LANGUAGES ────────────────────────────────────────────────────────────
      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box key="languages" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              LANGUAGES
            </Typography>
            {savedLanguages.map((lang, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px` }}>
                {lang.name}{lang.level ? ` – ${lang.level}` : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── ACCOMPLISHMENTS ──────────────────────────────────────────────────────
      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box key="accomplishments" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              ACCOMPLISHMENTS
            </Typography>
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px`, whiteSpace: "pre-line" }}>
                • {acc}
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── CERTIFICATIONS ───────────────────────────────────────────────────────
      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box key="certifications" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              CERTIFICATIONS
            </Typography>
            {savedCertifications.map((cert, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px` }}>
                {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── SOFTWARE SKILLS ──────────────────────────────────────────────────────
      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <Box key="softwareSkills" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              SOFTWARE SKILLS
            </Typography>
            {softwareSkills.map((skill, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px` }}>
                {skill.name} — {skill.level}%
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── VOLUNTEERING ─────────────────────────────────────────────────────────
      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box key="volunteering" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              VOLUNTEERING
            </Typography>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing / 2}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={{ ...txtSx, fontWeight: "bold" }}>{vol.subtopic}</Typography>
                <Typography sx={{ ...txtSx, color: isSidebar ? "rgba(255,255,255,0.8)" : "gray" }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={{ ...txtSx, whiteSpace: "pre-line", mt: 0.5 }}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      // ── INTERESTS ────────────────────────────────────────────────────────────
      case 'interests':
        return savedInterests.length > 0 ? (
          <Box key="interests" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              INTERESTS
            </Typography>
            {savedInterests.map((interest, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px` }}>
                • {interest}
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── WEBSITES ─────────────────────────────────────────────────────────────
      case 'websites': {
        const activeWebs = (savedWebsites && savedWebsites.filter(Boolean).length > 0)
          ? savedWebsites.filter(Boolean)
          : (data.website ? [data.website] : []);
        return activeWebs.length > 0 ? (
          <Box key="websites" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: mergedFont }}>
              WEBSITES & PROFILES
            </Typography>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              {activeWebs.map((site, index) => {
                if (!site) return null;
                const url = typeof site === 'string' ? site : (site.url || site.link || '');
                const name = typeof site === 'object' ? (site.label ? `${site.label.replace(/_/g, ' ')}: ${url}` : url) : url;
                return (
                  <Typography
                    key={index}
                    sx={{
                      ...txtSx,
                      mb: `${paragraphSpacing / 2}px`,
                      wordBreak: "break-all",
                      overflowWrap: "break-word",
                    }}
                  >
                    •{' '}
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: isSidebar ? 'white' : 'inherit',
                        textDecoration: 'none',
                      }}
                    >
                      {name || url}
                    </a>
                  </Typography>
                );
              })}
            </Box>
          </Box>
        ) : null;
      }

      // ── ADDITIONAL INFO ──────────────────────────────────────────────────────
      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box key="additionalInfo" sx={sectionWrapSx}>
            <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
              ADDITIONAL INFORMATION
            </Typography>
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px` }}>
                • {info}
              </Typography>
            ))}
          </Box>
        ) : null;

      // ── CUSTOM SECTIONS ──────────────────────────────────────────────────────
      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          return items.length > 0 ? (
            <Box key={sectionId} sx={sectionWrapSx}>
              <Typography variant="h5" sx={{ ...getSectionHeaderStyle(headingColor), color: headingColor, fontFamily: font }}>
                {customSectionName.toUpperCase()}
              </Typography>
              {items.map((item, i) => (
                <Typography key={i} sx={{ ...txtSx, mb: `${paragraphSpacing / 2}px`, whiteSpace: "pre-line" }}>
                  • {item}
                </Typography>
              ))}
            </Box>
          ) : null;
        }
        return null;
    }
  };

  // ─── RENDER HELPERS ──────────────────────────────────────────────────────────

  const renderSidebarSections = () =>
    leftSectionOrder.map(id => renderSection(id, true)).filter(Boolean);

  const renderRightContentSections = () =>
    rightSectionOrder.map(id => renderSection(id, false)).filter(Boolean);

  const getSectionTitle = (sectionId) => {
    const titles = {
      contact: 'Contact', expertise: 'Expertise', education: 'Education',
      profile: 'Professional Profile', workExperience: 'Work Experience',
      languages: 'Languages', accomplishments: 'Accomplishments',
      certifications: 'Certifications', softwareSkills: 'Software Skills',
      volunteering: 'Volunteering', interests: 'Interests',
      references: 'References', additionalInfo: 'Additional Information'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', fontFamily: font, ...globalFontStyle }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Side (Dark Sidebar)' : 'Right Side (Main Content)'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1, fontFamily: font, ...globalFontStyle }}>
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, fontFamily: font, ...globalFontStyle }}>
              {getSectionTitle(sectionId)}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: font, ...globalFontStyle }}>
              {index + 1}
            </Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center', fontFamily: font, ...globalFontStyle }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button
        onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder}
        variant="outlined" size="small" sx={{ mt: 1, fontFamily: font, ...globalFontStyle }}
      >
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // ─── PAGINATION ──────────────────────────────────────────────────────────────

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const mainSections = Array.from(mainContentRef.current.querySelectorAll('.resume-section'));
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const mainAvailableHeight = pageHeightInPx - (2 * mergedTopBottomMargin);

    let newPages = [];
    let currentMainPageSections = [];
    let currentMainHeight = 0;

    if (mainSections.length === 0) newPages.push({ main: [] });

    mainSections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      if (currentMainHeight + sectionHeight > mainAvailableHeight) {
        newPages.push({ main: currentMainPageSections });
        currentMainPageSections = [section.outerHTML];
        currentMainHeight = sectionHeight;
      } else {
        currentMainPageSections.push(section.outerHTML);
        currentMainHeight += sectionHeight;
      }
    });

    if (currentMainPageSections.length > 0) newPages.push({ main: currentMainPageSections });
    setPages(newPages);
  }, [page.height, mergedTopBottomMargin]);

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent.replace(/<(div|p|span)([^>]*)/g, (match, tagName, attrs) => {
      const styleRegex = /style="([^"]*)"/;
      const styleMatch = attrs.match(styleRegex);
      const defaultStyles = `font-family: ${mergedFont}; font-style: ${mergedFontStyle}; line-height: ${mergedLineSpacing}px; word-break: break-word; overflow-wrap: break-word;`;
      if (styleMatch) {
        const existingStyle = styleMatch[1];
        const needsFontSize = !existingStyle.includes('font-size:');
        const fontSizeStyle = needsFontSize ? `font-size: ${mergedFontSize};` : '';
        const mergedStyle = `style="${defaultStyles} ${fontSizeStyle} ${existingStyle}"`;
        return `<${tagName}${attrs.replace(styleRegex, mergedStyle)}`;
      } else {
        return `<${tagName}${attrs} style="${defaultStyles} font-size: ${mergedFontSize};"`;
      }
    });
  };

  useEffect(() => { paginateContent(); }, [paginateContent]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 100);
    return () => clearTimeout(id);
  }, [
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, customSections, leftSectionOrder, rightSectionOrder,
    mergedColor, mergedFont, mergedFontSize, mergedFontStyle, mergedHeadingSize, mergedSectionSpacing,
    mergedParagraphSpacing, mergedLineSpacing, mergedTopBottomMargin, mergedSideMargins, mergedLineWeight, mergedPageSize,
  ]);

  // ─── INVISIBLE MEASUREMENT CONTAINER ─────────────────────────────────────────

  const renderMainContentToMeasure = () => (
    <Box ref={mainContentRef} sx={{ visibility: 'hidden', position: 'absolute', width: "65%", overflow: "hidden", ...globalFontStyle }}>
      <Box sx={{ p: `${mergedTopBottomMargin}px ${mergedSideMargins}px`, width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
        {rightSectionOrder.map(sectionId => (
          <div className="resume-section" key={sectionId} style={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
            {renderSection(sectionId, false)}
          </div>
        ))}
      </Box>
    </Box>
  );

  // ─── SIDEBAR ─────────────────────────────────────────────────────────────────

  const renderSidebar = (isFirstPage = false) => {
    const commonSx = {
      color: "white",
      width: "35%",
      minHeight: page.height,
      // KEY FIX: contain all sidebar content within the 35% column
      overflow: "hidden",
      boxSizing: "border-box",
      "@media print": {
        color: "white",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
        minHeight: page.height,
        overflow: "hidden",
      },
    };

    if (!isFirstPage) {
      return (
        <Grid item xs={4.2} className="resume-sidebar-column" sx={commonSx}>
          <Box sx={{ minHeight: "100%" }} />
        </Grid>
      );
    }

    return (
      <Grid item xs={4.2} className="resume-sidebar-column" sx={{
        ...commonSx,
        display: "flex",
        flexDirection: "column",
        ...globalFontStyle,
        "@media print": {
          ...commonSx["@media print"],
          ...globalFontStyle,
        },
      }}>
        {leftSectionOrder.map((sectionId) => (
          <React.Fragment key={sectionId}>
            {renderSection(sectionId, true)}
          </React.Fragment>
        ))}
      </Grid>
    );
  };

  // ─── MAIN CONTENT ─────────────────────────────────────────────────────────────

  const renderMainContent = (pageContent) => {
    if (!pageContent || !pageContent.main) return null;
    return (
      <Grid item xs={7.8} className="resume-main-column" sx={{
        width: "65%",
        // KEY FIX: hard clip so no child can bleed outside
        overflow: "hidden",
        boxSizing: "border-box",
        ...globalFontStyle,
        "@media print": {
          overflow: "hidden",
          ...globalFontStyle,
        },
      }}>
        <div
          style={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}
          dangerouslySetInnerHTML={{
            __html: addFontToHtmlContent(pageContent.main.join(''))
          }}
        />
      </Grid>
    );
  };

  // ─── ROOT RENDER ──────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : 5, ...globalFontStyle }}>
      <style>
        {`
          .resume-sidebar-column {
            padding: ${mergedTopBottomMargin}px ${mergedSideMargins}px !important;
            background-color: ${mergedColor} !important;
          }
          .resume-main-column {
            padding: ${mergedTopBottomMargin}px ${mergedSideMargins}px !important;
          }
        `}
      </style>

      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, '@media print': { display: 'none' }, ...globalFontStyle }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
              <ChevronLeft />
            </IconButton>
            <Typography sx={{ mx: 2, ...globalFontStyle }}>
              Page {currentPage + 1} of {Math.max(1, pages.length)}
            </Typography>
            <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage >= Math.max(0, pages.length - 1)} color="primary">
              <ChevronRight />
            </IconButton>
            {enableDragDrop && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary" sx={{ ...globalFontStyle }}>
                  Arrange Sections
                </Button>
                <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small" sx={{ ...globalFontStyle }}>
                  Reset All Sections
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {renderMainContentToMeasure()}

      {/* Resume pages */}
      <Box id="resume-container" sx={{ ...globalFontStyle }}>
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                width: page.width,
                minHeight: page.height,
                mx: "auto",
                bgcolor: "#fff",
                boxShadow: index === currentPage ? 3 : 0,
                display: exporting ? 'flex' : (index === currentPage ? 'flex' : 'none'),
                flexDirection: "column",
                pageBreakAfter: "always",
                // KEY FIX: page-level overflow clip
                overflow: "hidden",
                boxSizing: "border-box",
                ...globalFontStyle,
                "@media print": {
                  width: page.width,
                  height: "auto",
                  minHeight: page.height,
                  boxShadow: "none",
                  m: 0,
                  display: "flex",
                  overflow: "hidden",
                  ...globalFontStyle,
                },
              }}
            >
              <Grid container sx={{ height: "100%", overflow: "hidden" }}>
                {renderSidebar(index === 0)}
                {renderMainContent(pageContent)}
              </Grid>
            </Box>
          ))
        ) : (
          // Fallback empty page
          <Box
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: 3,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
              ...globalFontStyle,
              "@media print": {
                width: page.width,
                height: "auto",
                minHeight: page.height,
                boxShadow: "none",
                m: 0,
                ...globalFontStyle,
              },
            }}
          >
            <Grid container sx={{ height: "100%", overflow: "hidden" }}>
              {renderSidebar(true)}
              <Grid item xs={7.8} className="resume-main-column" sx={{
                width: "65%",
                overflow: "hidden",
                boxSizing: "border-box",
                ...globalFontStyle,
              }}>
                <Typography sx={{ ...globalFontStyle }}>Loading resume...</Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" sx={{ ...globalFontStyle }}>
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ...globalFontStyle }}>
            Drag and drop sections to reorder them within each side or move sections between left and right sides.
            <br />
            <strong>Any section can be placed on either side!</strong>
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary" sx={{ ...globalFontStyle }}>Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained" sx={{ ...globalFontStyle }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume31;